// 2026-06-30 - FormuLearning - 관리자: 호출 단건 상세(요청/응답 원문) (REQ-FN-ADMIN-04)
// 파일 위치: frontend/src/features/admin/CallDetailModal.tsx

import { useEffect, useRef, useState } from 'react'
import { getCallDetail } from '../../api/admin'
import type { CallLogDetail } from '../../types/admin'
import { opLabel } from '../../types/admin'

interface Props {
  callId: string | null
  onClose: () => void
  onOpenGroup?: (groupId: string) => void
}

type JsonMode = 'pretty' | 'compact' | 'raw' | 'readable'

// raw 모드용 원문 추출: 문자열·{raw}·{_preview}는 그 텍스트를 그대로(이스케이프 해제),
// 그 외 객체는 최소화 JSON 문자열을 평문으로 노출.
function rawText(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    if (typeof o.raw === 'string') return o.raw
    if (typeof o._preview === 'string') return o._preview
  }
  return JSON.stringify(value)
}

// JSON 안에서 가장 긴 문자열 잎값과 그 경로를 찾는다(Vertex prompt/text, KIPRIS raw XML 등).
// 경로는 dot 표기(중첩·배열 인덱스 포함): 예 "raw", "prompt", "a.b.0.c".
function findLongest(value: unknown): { text: string | null; path: string } {
  let best: string | null = null
  let bestPath = ''
  const walk = (v: unknown, path: string) => {
    if (typeof v === 'string') {
      if (best === null || v.length > best.length) { best = v; bestPath = path }
    } else if (Array.isArray(v)) {
      v.forEach((x, i) => walk(x, path ? `${path}.${i}` : `${i}`))
    } else if (v && typeof v === 'object') {
      Object.entries(v as Record<string, unknown>).forEach(([k, x]) => walk(x, path ? `${path}.${k}` : k))
    }
  }
  walk(value, '')
  return { text: best, path: bestPath }
}

// 경량 XML 들여쓰기(태그 경계에서 줄바꿈 후 깊이만큼 패딩).
function formatXml(xml: string): string {
  const withBreaks = xml.replace(/>\s*</g, '>\n<').trim()
  let pad = 0
  return withBreaks.split('\n').map((raw) => {
    const line = raw.trim()
    if (/^<\/\w/.test(line)) pad = Math.max(pad - 1, 0)          // 닫는 태그: 먼저 줄임
    const out = '  '.repeat(pad) + line
    if (/^<\w[^>]*[^/]>$/.test(line) && !/^<\?/.test(line)) pad += 1  // 여는 태그(자기완결·선언 제외): 다음 줄 증가
    return out
  }).join('\n')
}

// readable 모드: 최장 텍스트가 XML/장문이면 그것만 예쁘게, 아니면 pretty JSON.
function readable(value: unknown): { kind: 'xml' | 'text' | 'json'; body: string; path: string } {
  if (value == null) return { kind: 'json', body: '—', path: '' }
  const { text, path } = findLongest(value)
  if (text && (text.includes('\n') || text.trim().startsWith('<') || text.length > 200)) {
    if (text.trim().startsWith('<')) return { kind: 'xml', body: formatXml(text), path }
    return { kind: 'text', body: text, path }
  }
  return { kind: 'json', body: JSON.stringify(value, null, 2), path: '' }
}

function render(value: unknown, mode: JsonMode): string {
  if (value == null) return '—'
  if (mode === 'pretty') return JSON.stringify(value, null, 2)
  if (mode === 'compact') return JSON.stringify(value)
  if (mode === 'raw') return rawText(value)
  return readable(value).body
}

function JsonBlock({ value, mode }: { value: unknown; mode: JsonMode }) {
  const info = mode === 'readable' && value != null ? readable(value) : null
  return (
    <div>
      {info && info.kind !== 'json' && (
        <span className="mb-1 inline-block rounded px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}>
          <span className="uppercase">{info.kind === 'xml' ? 'XML' : 'text'}</span>
          {' · from '}
          <span className="font-mono">{info.path || '(root)'}</span>
        </span>
      )}
      <pre
        className="max-h-[55vh] overflow-auto whitespace-pre-wrap break-words rounded p-3 text-xs leading-relaxed text-text-body"
        style={{ backgroundColor: 'var(--color-bg-panel)' }}
      >
        {render(value, mode)}
      </pre>
    </div>
  )
}

export default function CallDetailModal({ callId, onClose, onOpenGroup }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [detail, setDetail] = useState<CallLogDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [jsonMode, setJsonMode] = useState<JsonMode>('readable')

  useEffect(() => {
    if (!callId) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [callId, onClose])

  useEffect(() => {
    if (!callId) { setDetail(null); return }
    setLoading(true)
    getCallDetail(callId).then(setDetail).finally(() => setLoading(false))
  }, [callId])

  if (!callId) return null

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose() }}
    >
      <div className="max-h-[94vh] w-[75vw] max-w-[1400px] overflow-y-auto rounded-lg bg-bg-card p-6" style={{ boxShadow: 'var(--elev-2)' }}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text-primary">Call detail</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-body">✕</button>
        </div>

        {loading || !detail ? (
          <p className="text-sm text-text-secondary">Loading…</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-3 gap-4">
              {/* 비즈니스 정보 */}
              <section>
                <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary" style={{ color: 'var(--color-accent-purple)' }}>Business</h3>
                <dl className="flex flex-col gap-1 text-sm">
                  <div><dt className="inline text-text-secondary">User: </dt><dd className="inline text-text-body" title={detail.user_email ?? ''}>{detail.user_name ?? '—'}</dd></div>
                  <div><dt className="inline text-text-secondary">Project: </dt><dd className="inline text-text-body">{detail.project_name ?? '—'}</dd></div>
                  <div><dt className="inline text-text-secondary">Step: </dt><dd className="inline text-text-body">{detail.step ?? '—'}</dd></div>
                  <div><dt className="inline text-text-secondary">Substep: </dt><dd className="inline text-text-body">{detail.substep ?? '—'}</dd></div>
                  <div>
                    <dt className="inline text-text-secondary">Operation: </dt>
                    <dd className="inline text-text-body">{opLabel(detail.operation)}</dd>
                    {detail.request_group_id && (
                      <>
                        {' '}
                        <button
                          onClick={() => onOpenGroup?.(detail.request_group_id!)}
                          title={`이 오퍼레이션 실행 1회(id ${detail.request_group_id})의 모든 API 호출 보기`}
                          className="font-mono text-[11px] underline decoration-dotted underline-offset-2"
                          style={{ color: 'var(--color-accent-teal)' }}
                        >
                          ({detail.request_group_id.slice(0, 8)}) view calls
                        </button>
                      </>
                    )}
                  </div>
                </dl>
              </section>

              {/* API 정보 */}
              <section>
                <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-accent-purple)' }}>API</h3>
                <dl className="flex flex-col gap-1 text-sm">
                  <div><dt className="inline text-text-secondary">API id: </dt><dd className="inline font-mono text-xs text-text-primary">{detail.api_code}</dd></div>
                  <div><dt className="inline text-text-secondary">API name: </dt><dd className="inline text-text-body">{detail.api_name}</dd></div>
                  <div><dt className="inline text-text-secondary">Provider: </dt><dd className="inline text-text-body">{detail.provider}</dd></div>
                  <div>
                    <dt className="text-text-secondary">URL: </dt>
                    <dd className="mt-0.5 break-all font-mono text-[11px] text-text-body">{(detail.request?.url as string) ?? '—'}</dd>
                  </div>
                </dl>
              </section>

              {/* 호출 정보 */}
              <section>
                <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-accent-purple)' }}>Call</h3>
                <dl className="flex flex-col gap-1 text-sm">
                  <div><dt className="inline text-text-secondary">Called at: </dt><dd className="inline text-text-body">{new Date(detail.called_at).toLocaleString()}</dd></div>
                  <div><dt className="inline text-text-secondary">Duration: </dt><dd className="inline text-text-body">{detail.duration_ms != null ? `${detail.duration_ms} ms` : '—'}</dd></div>
                  <div><dt className="inline text-text-secondary">Sent: </dt><dd className="inline text-text-body">{detail.request_bytes != null ? `${detail.request_bytes} B` : '—'}</dd></div>
                  <div>
                    <dt className="inline text-text-secondary">Received: </dt>
                    <dd className="inline text-text-body">{detail.response_bytes != null ? `${detail.response_bytes} B` : '—'}{detail.truncated ? ' *' : ''}</dd>
                  </div>
                  <div><dt className="inline text-text-secondary">Status: </dt><dd className="inline" style={{ color: detail.success ? 'var(--color-accent-teal)' : 'var(--color-accent-purple)' }}>{detail.success ? 'OK' : 'FAIL'} {detail.response_status ?? ''}</dd></div>
                </dl>
              </section>
            </div>

            {!detail.success && (
              <div className="rounded p-3 text-xs" style={{ backgroundColor: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>
                <span className="font-semibold">Error{detail.response_status ? ` (HTTP ${detail.response_status})` : ''}: </span>
                {detail.error_message || 'Call failed — see response below.'}
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">View:</span>
              {(['readable', 'pretty', 'compact', 'raw'] as JsonMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setJsonMode(m)}
                  className="rounded px-2 py-0.5 text-xs capitalize transition-colors"
                  style={
                    jsonMode === m
                      ? { backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
                      : { color: 'var(--color-text-secondary)' }
                  }
                >
                  {m}
                </button>
              ))}
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-text-secondary">
                Request <span className="font-normal">(auth keys masked · {detail.request_kind ?? '—'})</span>
              </p>
              <JsonBlock value={detail.request} mode={jsonMode} />
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-text-secondary">
                Response {detail.truncated && <span className="ml-1 rounded px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>stored truncated &gt;64KB</span>}
              </p>
              <JsonBlock value={detail.response} mode={jsonMode} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
